import { genId } from "../../../../core/services/genId";
import { Alert, AlertType } from "./Alerts.model";

export const UNMAPPED_USDW = "Not mapped";

export interface CasingPlanComponent {
    wellType: string;
    wellTypeDescription: string;
    formations: FormationModel[]
    casingPlan: CasingPlanModel;
}

export class CasingPlanModel {
    id: string = "";
    affirmedComplete: boolean;
    alerts: AlertStatus[] = [];
    civilTownship: string = "";
    companyName: string = "";
    county: string = "";
    createdBy: string = "";
    creationDate: Date = new Date();    
    description: string = "";
    districtPhone: string = "";
    isUrbanizedArea: boolean = false;
    lastModifiedDate: Date = new Date();
    locationSite: string = "";
    newWell: boolean = true;
    notes: string = "";
    permitId: string = "";
    proposedTotalDepth: number;
    project: string = "";
    referenceNumber: string = "";
    regulationsConfirmed: boolean = false;
    schematic: string = "";
    surfaceFootageLocation: string = "";    
    surfaceLatitude: number;
    surfaceLongitude: number;
    title: string = "";
    wellDirection: WellDirection;
    wellSegments: WellSegmentModel[] = [];
    wellType: string = "";
    wellTypeDescription: string = "";
    
    constructor(newId: string = genId()) {
        this.id = newId;
    }
}

export class AlertStatus {
    alertType: AlertType;
    applicable: boolean;
    formationThickness: number;
    name: string;
    resolved: boolean;
    text: string;

    constructor(alert: Alert) {
        this.alertType = alert.alertType;
        this.applicable = alert.applicable;
        this.name = alert.name;
        this.resolved = alert.resolved;
        this.text = alert.text;
        this.formationThickness = alert.thickness;
    }

}

export class WaterSource {
    id: string = "";
    name: string = "";
    recycledWaterVolume: number = 0;
    estimatedTotalVolume: number = 0;
    estimatedWithdrawRate: number = 0;
    watershed: string = "";
    latitude: number = 0;
    longitude: number = 0;
    source: string = "";
}

export enum WellDirection {
    Vertical = "Vertical",
    Horizontal = "Horizontal",
    Directional = "Directional",
    OtherHorizontal = "Other Horizontal"
}

export class WellSegmentModel {
    id: string;
    index?: number;
    casingType: CasingTypeModel;
    casingCondition?: CasingConditionModel;
    drillingEquipment?: DrillingEquipmentModel;
    boreholeDiameter: number;
    boreholeDepth?: number;
    boreholeTop?: number;
    boreholeBottom: number;
    casingDiameter?: number;
    casingTop?: number;
    casingBottom?: number;
    casingLength?: number;
    casingGrade?: string;
    casingWeight?: number;
    casingShoeDepth?: number;
    numberCentralizers?: number;
    floatCollarDepth?: number;
    basketDepth?: number;
    packerDepth?: number;
    dvToolDepth?: number;
    cementClass?: string;
    cementVolume?: number;
    slurryYield?: number;
    cementTop?: number;
    cementBottom?: number;
    formation?: FormationModel;
    perforationTop?: number;
    perforationBottom?: number;
    stageNumber?: number;
    numberOfStages?: number;
    shotsPerStage?: number;
    zoneTestedOrProduced?: boolean;
    tested?: boolean;
    produced?: boolean;
    injected?: boolean;
    hydraulicFracturing?: boolean;
    toBePulled?: boolean;
    hasAngles?: boolean;
    detailDiagram?: string;
    position?: number;
    hasData?: boolean;


    gisObjects: WellSegmentGisDataModel[] = [];
    isComplete: boolean;

    constructor() {
        this.id = genId();
        this.zoneTestedOrProduced = false;
        this.tested = false;
        this.produced = false;
        this.injected = false;
        this.hydraulicFracturing = false;
        this.casingType = new CasingTypeModel();
        this.casingCondition = new CasingConditionModel();
        this.drillingEquipment = new DrillingEquipmentModel();
        this.formation = new FormationModel();
        // Default Values for calculating amount of cement
        this.slurryYield = SlurryYield.CLASS_A;
        this.cementClass = "Class A";
    }
}

export enum SlurryYield {
    CLASS_A = 1.18
}

export class CasingTypeModel {
    code: string;
	definition: CasingType | string;
    isActive: boolean;
    isRequired: boolean;
    lastModifiedDateUTC?: any;
    tags?: string;
}

// THESE WERE WRITTEN AS IS TO MATCH DATA IMPORTED FROM ODNR
export enum CasingType {
    Default = " ",
    DrivePipe = "Drive Pipe",
    Conductor = "Conductor",
    Concentric = "Concentric",
    OpenHole = "Open Hole",
    Production = "Production Casing",
    Tubing = "Tubing",
    MineString = "Mine String",
    Surface = "Surface Casing",
    Intermediate1Entry = "Intermediate 1 Field Entry",
    Intermediate1 = "Intermediate 1 Casing",
    Intermediate2Entry = "Intermediate 2 Field Entry",
    Intermediate2 = "Intermediate 2 Casing",
    Liner1 = "Liner 1",
    Liner1FieldEntry = "Liner 1 Field Entry"
}

export class CasingConditionModel {
    id: string;    
    name: string;
    reconditionAcknowledged?: string;
    reconditionAccepted?: boolean;
}

export class DrillingEquipmentModel {
    code: string;
    definition: string;
    isActive: boolean;
    isRequired: boolean;
    lastModifiedDateUTC: any;
}

export class FormationModel {
    code: string;
    definition: string;
    isActive: boolean;
    isRequired: boolean;
	lastModifiedDateUTC: any;
	aapgCode?: string;
}

export class WellSegmentGisDataModel {

    point: string = "Start";
    md?: number;
    inclination?: number;
    azimuth?: number;
    tvd: number = 0;
    vertSect?: number;
    north?: number;
    east?: number;
    gridEast?: number;
    gridNorth?: number;
    latitude: number;
    longitude: number;
    closureDistance?: number;
    closureDirection?: number;
    dls?: number;
    buildRate?: number;
    turnRate?: number;
}

export class Position {
    left: string;
    top: string;
}

export interface WellConfigurationData {
    index: WellConfigurationIndex;
    displayName: string;
    location: string;
    multipleLocations: string[],
    locationType: GisContextLocationType
}

export enum WellConfigurationIndex {
    IsComplete,
    WellDirection,
    LatitudeDirectional,
    LatitudeHorizontal,
    LatitudeVertical,
    LongitudeDirectional,
    LongitudeHorizontal,
    LongitudeVertical,
    StatePlane,
    StatePlaneVertical,
    StatePlaneHorizontal,
    StatePlaneDirectional,
    WellName,
    WellNumber,
    ApplicationNumber,
    UrbanArea,
    WaterSourceLatitude,
    WaterSourceLongitude,
    SectionLotTract,
    Section,
    Lot,
    Tract,
    QuadrangleName,
    CountyName,
    TownshipName,
    Watershed,
    WellheadToWellheadVariance,
    State,
    ConditionBodiesOfWater,
    ConditionDriftThickness,
    ConditionHundredYearFloodPlain,
    ConditionHydrogenSulfide,
    ConditionInjectionWellAOR,
    ConditionLakePlain,
    ConditionMeccaPool,
    ConditionAbandonedMines,
    ConditionActiveMines,
    ConditionMunicipalWater,
    ConditionRoadsRailroads,
    ConditionSeismicArea,
    ConditionShallowSurface,
    ConditionThinTill,
    ConditionUndergroundDrinkingWater,
    ConditionUrbanArea,
    ConditionWetlands,
    ConditionStateLands,
    SurfaceLatitude,
    SurfaceLongitude,
    FluidVolume,
    InjectionFluidType,
    WellType,
    TargetLatitude,
    TargetLongitude,
    QuadrangleCode,
    CountyCode,
    CalculatedLatitude,
    CalculatedLongitude,
    TownshipNumber,
    InspectorName,
    InspectorPhone,
    BackupInspectorName,
    BackupInspectorPhone,
    RegionalPhone,
    SupervisorName,
    SupervisorPhone,
    NewOrExistingWell,
    FirstTakeNADX,
    FirstTakeNADY,
    FirstTakeStatePlane,
    LastTakeNADX,
    LastTakeNADY,
    LastTakeStatePlane,
    TargetNADX,
    TargetNADY,
    TargetStatePlane,
    ProposedTotalDepth,
    ProposedFormation,
    ConditionGasStorage,
    ConditionCoalBearing,
    LatitudeNumeric,
    LongitudeNumeric,
    ConditionWaterWellProximity,
    ConditionPerennialStreams,
    ConditionSprings,
    WellTypeDescription,
}

export function ensureWellComponentConfig(type: WellConfigType, existingConfig: WellConfigurationData[]): any {
    let defaultConfig: any;
    switch (type) {
        case WellConfigType.NewCasingPlan:
            defaultConfig = [
                {
                    index: WellConfigurationIndex.IsComplete,
                    displayName: "Casing Plan is Complete",
                    location: "$.data.casingPlanComplete",
                    locationType: GisContextLocationType.ReadAndWrite
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.WellDirection,
                    displayName: "Well Direction",
                    location: "$.data.WellDirection",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.WellType,
                    displayName: "Well Type",
                    location: "$.data.WellType",
                    locationType: GisContextLocationType.ReadOnly,
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LatitudeHorizontal,
                    displayName: "Surface Latitude - Horizontal",
                    location: "$.data.hNAD83YSL",
                    locationType: GisContextLocationType.ReadOnly
                },
                {
                    index: WellConfigurationIndex.LongitudeHorizontal,
                    displayName: "Surface Longitude - Horizontal",
                    location: "$.data.hNAD83XSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData as WellConfigurationData,
                {
                    index: WellConfigurationIndex.StatePlaneHorizontal,
                    displayName: "State Plane - Horizontal",
                    location: "$.data.hStatePlaneSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.LatitudeDirectional,
                    displayName: "Surface Latitude - Directional",
                    location: "$.data.dNAD83YSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.LongitudeDirectional,
                    displayName: "Surface Longtitude - Directional",
                    location: "$.data.dNAD83XSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.StatePlaneDirectional,
                    displayName: "State Plane - Directional",
                    location: "$.data.dStatePlaneSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.LatitudeVertical,
                    displayName: "Surface Latitude - Vertical",
                    location: "$.data.vNAD83Y",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LongitudeVertical,
                    displayName: "Surface Longitude - Vertical",
                    location: "$.data.vNAD83X",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.StatePlaneVertical,
                    displayName: "State Plane - Vertical",
                    location: "$.data.vstatePlane",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.WellName,
                    displayName: "Well Name",
                    location: "$.data.wellName",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.WellNumber,
                    displayName: "Well Number",
                    location: "$.data.wellNumber",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.ApplicationNumber,
                    displayName: "Application Number",
                    location: "$.data.applicationNumber",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.NewOrExistingWell,
                    displayName: "New or Existing Well",
                    location: "$.data.newOrExisting",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.ProposedTotalDepth,
                    displayName: "Proposed Total Depth",
                    location: "$.data.propTotalDepth",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.UrbanArea,
                    displayName: "Urbanized Area",
                    location: "$.data.urbanArea",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.ProposedFormation,
                    displayName: "Proposed Formation",
                    location: "",
                    multipleLocations: [ "$.data.propFormation1", "$.data.propFormation2", "$.data.propFormation3", "$.data.propFormation4", "$.data.propFormation5" ],
                    locationType: GisContextLocationType.MultipleRead
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.WellTypeDescription,
                    displayName: "Well Type Description",
                    location: "$.data.WellTypeDescription",
                    locationType: GisContextLocationType.ReadOnly,
                } as WellConfigurationData,
            ] as WellConfigurationData[];
            break;
        case WellConfigType.Map:
            defaultConfig = [
                {
                    index: WellConfigurationIndex.WellDirection,
                    displayName: "Well Direction",
                    location: "$.data.WellDirection",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.LatitudeDirectional,
                    displayName: "Surface Latitude - Directional",
                    location: "$.data.dNAD83YSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.LongitudeDirectional,
                    displayName: "Surface Longtitude - Directional",
                    location: "$.data.dNAD83XSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.StatePlaneDirectional,
                    displayName: "State Plane - Directional",
                    location: "$.data.dstatePlaneSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LatitudeHorizontal,
                    displayName: "Surface Latitude - Horizontal",
                    location: "$.data.hNAD83YSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LongitudeHorizontal,
                    displayName: "Surface Longitude - Horizontal",
                    location: "$.data.hNAD83XSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.StatePlaneHorizontal,
                    displayName: "State Plane - Horizontal",
                    location: "$.data.hstatePlaneSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LatitudeVertical,
                    displayName: "Surface Latitude - Vertical",
                    location: "$.data.vNAD83Y",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LongitudeVertical,
                    displayName: "Surface Longitude - Vertical",
                    location: "$.data.vNAD83X",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.StatePlaneVertical,
                    displayName: "State Plane - Vertical",
                    location: "$.data.vstatePlane",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData
            ] as WellConfigurationData[];
            break;
        case WellConfigType.AreaOfReviewMap:
            defaultConfig = [
                {
                    index: WellConfigurationIndex.WellDirection,
                    displayName: "Well Direction",
                    location: "$.data.wellDirection",
                    locationType: GisContextLocationType.ReadOnly
                },
                {
                    index: WellConfigurationIndex.LatitudeDirectional,
                    displayName: "Latitude - Directional",
                    location: "$.data.dNAD83YSL",
                    locationType: GisContextLocationType.ReadOnly

                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LongitudeDirectional,
                    displayName: "Longitude - Directional",
                    location: "$.data.dNAD83XSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.StatePlaneDirectional,
                    displayName: "State Plane - Directional",
                    location: "$.data.dstatePlaneSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LatitudeHorizontal,
                    displayName: "Latitude - Horizontal",
                    location: "$.data.hNAD83YSL",
                    locationType: GisContextLocationType.ReadOnly

                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LongitudeHorizontal,
                    displayName: "Longitude - Horizontal",
                    location: "$.data.hNAD83XSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.StatePlaneHorizontal,
                    displayName: "State Plane - Horizontal",
                    location: "$.data.hstatePlaneSL",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LatitudeVertical,
                    displayName: "Latitude - Vertical",
                    location: "$.data.vNAD83Y",
                    locationType: GisContextLocationType.ReadOnly

                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.LongitudeVertical,
                    displayName: "Longitude - Vertical",
                    location: "$.data.vNAD83X",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.StatePlaneVertical,
                    displayName: "State Plane - Vertical",
                    location: "$.data.vstatePlane",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.FluidVolume,
                    displayName: "Fluid Volume",
                    location: "$.data.volume",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData,
                {
                    index: WellConfigurationIndex.InjectionFluidType,
                    displayName: "Injection Type",
                    location: "$.data.injectionType",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.WellType,
                    displayName: "Well Type",
                    location: "$.data.wellType",
                    locationType: GisContextLocationType.ReadOnly
                } as WellConfigurationData, {
                    index: WellConfigurationIndex.WellTypeDescription,
                    displayName: "Well Type Description",
                    location: "$.data.WellTypeDescription",
                    locationType: GisContextLocationType.ReadOnly
                }
            ] as WellConfigurationData[];
            break;
        default:
            defaultConfig = [];
            break;
    }
    let config: WellConfigurationData[] = [];
    for (let c = 0; c < defaultConfig.length; c++) {
        let value: WellConfigurationData = undefined;
        if (existingConfig) {
            value = existingConfig.find(cf => cf.index === defaultConfig[c].index);
        }
        if (value) {
            value.locationType = defaultConfig[c].locationType;
            value.displayName = defaultConfig[c].displayName;
        } else {
            value = defaultConfig[c];
            if (value.locationType === GisContextLocationType.MultipleRead && value.multipleLocations.length === 0) {
                value.multipleLocations.push(" ");
            }
        }
        config.push(value);
    }


    return config;
}

export function getContextLocation(config: WellConfigurationData[], location: WellConfigurationIndex): string {
    let loc = config.find(d => d.index === location).location;
    if (loc != null && loc.length > 0 && loc.endsWith(".value") === false) {
        loc = `${loc}.value`;    
    }
    return loc;
}

export function getMultipleReadContextLocations(config: WellConfigurationData[], location: WellConfigurationIndex): string[] {
    const list: string[] = [];
    let setting = config.find(d => d.index === location);
    if (setting) {
        const values = setting.multipleLocations;        
        for (let s = 0; s < values.length; s++) {
            let loc = values[s];
            if (loc.length < 6 || loc.substring(loc.length - 6, loc.length - 1) !== ".value") {
                loc = `${loc}.value`;
            }
            list.push(loc);
        }
    }
    return list;
}

export enum WellConfigType {
    NewCasingPlan = 0,
    Map = 1,
    AreaOfReviewMap = 2
}

export enum StatePlane {
    South = "spsouth",
    North = "spnorth"
}

export enum GisContextLocationType {
    ReadOnly,
    ReadAndWrite,
    LocationWrite,
    ConditionWrite,//LocationWrite vs. ConditionWrite helps keep the UI organized for the GIS controller settings
    MultipleRead //MultipleRead is for properties that need to be able to read from multiple locations simultaneously for relevant values that all need to apply, such as multiple formation inputs 
}

export enum SegmentWizardSteps {
    Casing = "casing",
    Conditions = "conditions",
    Tools = "tools",
    Points = "points",
    Angles = "angles",
    AngleBuilder = "anglebuilder",
    ReviewWellSegment = "reviewWellSegment"
}

export enum CasingPlanSteps {
    Introduction = "introduction",
    Regulations = "regulations",
    AlertsOverview = "alertsOverview",
    WellSegmentWizard = "wellSegmentWizard",
    ReviewCasingPlan = "reviewCasingPlan"
}